import { debounce } from '@mui/material';
import * as monaco from 'monaco-editor';
import { useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import type { MonacoEditorRef } from '../../Components/Editor/Monaco/MonacoEditor';
import { projectSlice } from '../../redux/ProjectSlice';
import { EditorState } from '../../Components/CodeEditor/types';
import { DEFAULT_LSP_SESSION_OPTIONS } from '../../Components/Editor/Monaco/LspSession';
import { clearMountedMainProgramEditor } from '../../Components/CodeEditor/utils/mainProgramEditor';
import { resolvePyrightLocale } from '../../utils/language-config';
import { configureMonacoWorkerEnvironment } from './monacoWorkerEnvironment';
import { ensureProgramLspClientDisposedOnUnload, programLspClient } from './programLspClient';

export function useMonacoEditor() {
  const project = useAppSelector((state) => state.project.loadedProject);
  const dispatch = useAppDispatch();

  const editorRef = useRef<MonacoEditorRef | null>(null);
  const standaloneEditorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [editorInstance, setEditorInstance] = useState<monaco.editor.IStandaloneCodeEditor | null>(null);
  const previewModelRef = useRef<monaco.editor.ITextModel | null>(null);

  const [editorState, setEditorState] = useState<EditorState>(() => {
    const locale = resolvePyrightLocale(i18n.language);

    return {
      gotInitialState: false,
      settings: {
        ...DEFAULT_LSP_SESSION_OPTIONS,
        ...(locale ? { locale } : {}),
      },
      diagnostics: [],
      isWaitingForResponse: true,
      code: '',
    };
  });
  const latestCodeRef = useRef('');

  useEffect(() => {
    latestCodeRef.current = editorState.code;
  }, [editorState.code]);

  // Monaco environment setup - only run once on mount
  useEffect(() => {
    configureMonacoWorkerEnvironment();
  }, []);

  // Keep LSP alive for whole app session; dispose only on app unload
  useEffect(() => {
    ensureProgramLspClientDisposedOnUnload();
  }, []);

  useEffect(() => {
    const handleLanguageChange = (language: string) => {
      const locale = resolvePyrightLocale(language);

      setEditorState((prevState) => {
        if (prevState.settings.locale === locale) {
          return prevState;
        }

        const settings = { ...prevState.settings };
        delete settings.locale;
        if (locale) {
          settings.locale = locale;
        }

        return { ...prevState, settings };
      });
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const reduxDiagnostics = useAppSelector((state) => state.project.diagnostics);
  const linterDiagnosticsPending = useAppSelector((state) => state.project.linterDiagnosticsPending);

  useEffect(() => {
    setEditorState((prevState) => ({
      ...prevState,
      diagnostics: reduxDiagnostics,
      isWaitingForResponse: linterDiagnosticsPending,
    }));
  }, [reduxDiagnostics, linterDiagnosticsPending]);

  // Initialize editor state from project
  useEffect(() => {
    if (!editorState.gotInitialState && project) {
      if (project.program.code !== '') {
        programLspClient.updateCode(project.program.code);
      }

      setEditorState((prevState) => {
        return {
          ...prevState,
          gotInitialState: true,
          code: project.program.code,
          settings: editorState.settings,
        };
      });
    }
  }, [editorState.gotInitialState, editorState.settings, project?.program.code, project]);

  // Cleanup preview model on unmount
  useEffect(() => {
    const previewModel = previewModelRef.current;
    return () => {
      previewModel?.dispose();
    };
  }, []);

  useEffect(() => {
    return () => {
      clearMountedMainProgramEditor(standaloneEditorRef.current);
      standaloneEditorRef.current = null;
      setEditorInstance(null);
    };
  }, []);

  // Update LSP settings when they change
  useEffect(() => {
    programLspClient.updateSettings(editorState.settings);
  }, [editorState.settings]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Swallow command-s or ctrl-s to prevent browser save.
      if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Debounce the local editor-state sync, but keep Redux program text and
  // deploy metadata aligned with Monaco immediately so other UI controls do not
  // build whole-program replacements from stale code.
  const debouncedEditorStateSync = useRef(
    debounce((code: string) => {
      setEditorState((prevState) => ({ ...prevState, code }));
    }, 250)
  ).current;

  const debouncedSync = useRef((code: string) => {
    dispatch(projectSlice.actions.onUpdateLastModified(new Date().toISOString()));
    dispatch(projectSlice.actions.onUpdateProgram(code));
    debouncedEditorStateSync(code);
  }).current;

  const handleEditorMounted = (editor: monaco.editor.IStandaloneCodeEditor) => {
    standaloneEditorRef.current = editor;
    setEditorInstance(editor);
    window.__bps_editor = editor;
  };

  const onUpdateCode = (code: string) => {
    try {
      programLspClient.updateCode(code);
    } catch {
      // Ignore errors during code sync
    }
    debouncedSync(code);
  };

  return {
    editorState,
    setEditorState,
    editorRef,
    standaloneEditorRef,
    editorInstance,
    previewModelRef,
    handleEditorMounted,
    onUpdateCode,
    debouncedSync,
    lspClient: programLspClient,
  };
}
